/**
 * Dates shown in the business time zone (Eastern), whatever the viewer's browser is set to.
 *
 * Use for any value the API sends as a real instant: an ISO string ending in a true "Z" (Instant.toString()), or
 * epoch seconds / milliseconds (a bare Instant). Do NOT use for the older "Eastern wall time + fake Z" strings
 * (DateTimeUtils.covertUTCTimestampToString); those already read correctly through formatDateString.
 */
export const BUSINESS_TIME_ZONE = 'America/New_York';

/** A Date from an API instant: ISO string, epoch seconds or milliseconds, or [y, m, d]. Null when unknown. */
export function toInstant(v) {
  if (v === null || v === undefined || v === '') return null;
  if (v instanceof Date) return Number.isNaN(v.getTime()) ? null : v;
  if (typeof v === 'number') return new Date(v < 1e11 ? v * 1000 : v);
  if (Array.isArray(v)) return new Date(Date.UTC(v[0], (v[1] || 1) - 1, v[2] || 1, 12));
  // A date with no time ("2026-10-12", e.g. a LocalDate week start) is that calendar day: noon UTC keeps it on the
  // same day in Eastern. new Date() would read it as UTC midnight, which is the evening before in Eastern.
  const dateOnly = typeof v === 'string' && /^(\d{4})-(\d{2})-(\d{2})$/.exec(v);
  if (dateOnly) return new Date(Date.UTC(+dateOnly[1], +dateOnly[2] - 1, +dateOnly[3], 12));
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? null : d;
}

/** Formats an instant in Eastern with Intl options, e.g. { month: 'short', day: 'numeric' }. '' when unknown. */
export function formatEastern(v, options = { month: 'short', day: 'numeric', year: 'numeric' }) {
  const d = toInstant(v);
  return d ? new Intl.DateTimeFormat('en-US', { timeZone: BUSINESS_TIME_ZONE, ...options }).format(d) : '';
}

/** The Eastern calendar day of an instant as 'YYYY-MM-DD'. */
export function easternDay(v) {
  const d = toInstant(v);
  if (!d) return null;
  const parts = Object.fromEntries(
    new Intl.DateTimeFormat('en-US', { timeZone: BUSINESS_TIME_ZONE, year: 'numeric', month: '2-digit', day: '2-digit' })
      .formatToParts(d)
      .map((p) => [p.type, p.value])
  );
  return `${parts.year}-${parts.month}-${parts.day}`;
}

/** Whole Eastern calendar days from `since` to `now` (0 = same day there); null when unknown; never negative. */
export function easternDaysSince(since, now = new Date()) {
  const a = easternDay(since);
  const b = easternDay(now);
  if (!a || !b) return null;
  return Math.max(0, Math.round((Date.parse(`${b}T00:00:00Z`) - Date.parse(`${a}T00:00:00Z`)) / 86400000));
}
